import React, { useState, useRef, useCallback } from "react";
import { Platform, Pressable, View, Modal, TouchableWithoutFeedback, useWindowDimensions } from "react-native";
import { AppText } from "@/components/ui/AppText";
import { Icon } from "@/components/ui/Icon";
import { Feather } from "@expo/vector-icons";
import { cn } from "@/lib/utils";
import { useI18n } from "@/context/I18nContext";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DropdownMenuSection<T extends string> {
  /** Optional section header label rendered in small caps. */
  title?: string;
  items: DropdownMenuItem<T>[];
}

export interface DropdownMenuItem<T extends string> {
  value: T;
  label: string;
}

export interface DropdownMenuProps<T extends string> {
  /**
   * One or more sections. Use a single section without a `title` for a flat list.
   * Use multiple sections with titles for grouped menus (e.g. Alphabetically / Date).
   */
  sections: DropdownMenuSection<T>[];
  /** Currently selected value. The matching item shows a checkmark. */
  value: T;
  onSelect: (value: T) => void;
  /** Trigger element — rendered as the anchor for the dropdown. */
  trigger: React.ReactNode;
  /**
   * Width of the dropdown panel in logical pixels.
   * Defaults to 176 (w-44).
   */
  panelWidth?: number;
  /** testID forwarded to the dropdown panel for testing. */
  testID?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * A generic single-select dropdown menu.
 *
 * Renders a trigger element (any ReactNode — typically a `<Button>`).
 * Tapping the trigger toggles an absolutely-positioned panel below it.
 * Selecting an item fires `onSelect` and closes the panel.
 *
 * Sections with a `title` render a small-caps header above their items.
 * A hairline divider is rendered between consecutive sections.
 *
 * Use this for Filter and Sort panels or any single-select contextual menu.
 */
export function DropdownMenu<T extends string>({
  sections,
  value,
  onSelect,
  trigger,
  panelWidth = 176,
  testID,
}: DropdownMenuProps<T>) {
  const [open, setOpen] = useState(false);
  const [panelPos, setPanelPos] = useState<{
    triggerTop: number;
    triggerBottom: number;
    left: number;
    right: number;
  } | null>(null);
  const triggerRef = useRef<View>(null);
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const { isRTL } = useI18n();

  const handleTriggerPress = useCallback(() => {
    if (open) {
      setOpen(false);
      return;
    }
    // Open the panel immediately with a default position so that environments
    // without native layout APIs (e.g. RNTL in tests) still show the panel.
    // measureInWindow then updates the position with accurate screen coordinates.
    setPanelPos({ triggerTop: 0, triggerBottom: 0, left: 0, right: panelWidth });
    setOpen(true);
    triggerRef.current?.measureInWindow((x, y, width, height) => {
      if (x !== 0 || y !== 0 || width !== 0 || height !== 0) {
        setPanelPos({ triggerTop: y, triggerBottom: y + height, left: x, right: x + width });
      }
    });
  }, [open, panelWidth]);

  const handleSelect = (v: T) => {
    onSelect(v);
    setOpen(false);
  };

  return (
    <View ref={triggerRef}>
      <Pressable onPress={handleTriggerPress} testID={testID ? `${testID}-trigger` : undefined}>
        {trigger}
      </Pressable>

      <Modal
        visible={open}
        transparent
        animationType="none"
        onRequestClose={() => setOpen(false)}
        statusBarTranslucent
      >
        <TouchableWithoutFeedback onPress={() => setOpen(false)}>
          <View style={{ flex: 1 }}>
            {panelPos && (() => {
              const _spaceBelow = windowHeight - panelPos.triggerBottom - 16;
              const _spaceAbove = panelPos.triggerTop - 16;
              const _flipUp = _spaceBelow < 120 && _spaceAbove > _spaceBelow;
              //
              // Positioning is tricky because the Modal creates a new native root:
              //
              // • LTR (all platforms): no coordinate mirroring. Use CSS `right` to
              //   right-align the panel with the trigger's right edge.
              //   right = windowWidth - panelPos.right
              //
              // • RTL on NATIVE: I18nManager.forceRTL(true) causes iOS UIKit to apply
              //   coordinate mirroring to all newly created native views (including
              //   Modals opened after the call). In this mirrored system:
              //     `right: X` → UIKit maps frame to physical left position X
              //   So to place the panel adjacent to the trigger (at physical left panelPos.left),
              //   we use `right: panelPos.left`.
              //
              // • RTL on WEB: document.dir="rtl" is set, but CSS `left`/`right` in
              //   absolute positioning are always physical — no mirroring. Use `left: panelPos.left`.
              //
              const _posStyle: { left?: number; right?: number } = isRTL
                ? Platform.OS !== "web"
                  ? { right: Math.max(0, Math.min(panelPos.left, windowWidth - panelWidth)) }
                  : { left: Math.max(0, Math.min(panelPos.left, windowWidth - panelWidth)) }
                : { right: Math.max(0, Math.min(windowWidth - panelPos.right, windowWidth - panelWidth)) };
              return (
              <TouchableWithoutFeedback>
                <View
                  testID={testID}
                  style={{
                    position: "absolute",
                    top: _flipUp ? undefined : panelPos.triggerBottom + 4,
                    bottom: _flipUp ? windowHeight - panelPos.triggerTop + 4 : undefined,
                    ..._posStyle,
                    width: panelWidth,
                    backgroundColor: "white",
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: "#E5E7EB",
                    shadowColor: "#000",
                    shadowOffset: { width: 0, height: 2 },
                    shadowOpacity: 0.08,
                    shadowRadius: 8,
                    elevation: 8,
                    overflow: "hidden",
                  }}
                >
                  {sections.map((section, sectionIdx) => (
                    <View key={sectionIdx}>
                      {sectionIdx > 0 && (
                        <View
                          style={{
                            marginHorizontal: 12,
                            marginVertical: 4,
                            height: 1,
                            backgroundColor: "#E5E7EB",
                          }}
                        />
                      )}

                      {section.title != null && (
                        <View style={{ paddingHorizontal: 12, paddingTop: 12, paddingBottom: 4 }}>
                          <AppText
                            weight="medium"
                            className="text-xs text-supporting-text uppercase tracking-wider"
                          >
                            {section.title}
                          </AppText>
                        </View>
                      )}

                      {section.items.map((item) => {
                        const isSelected = item.value === value;
                        return (
                          <Pressable
                            key={item.value}
                            onPress={() => handleSelect(item.value)}
                            testID={testID ? `${testID}-item-${item.value}` : undefined}
                            className={`${isRTL ? "flex-row-reverse" : "flex-row"} items-center justify-between px-3 py-2.5`}
                          >
                            <AppText
                              weight={isSelected ? "semibold" : "regular"}
                              className={cn(
                                "text-base",
                                isSelected ? "text-dark-text-50" : "text-supporting-text"
                              )}
                            >
                              {item.label}
                            </AppText>
                            {isSelected && (
                              <Icon as={Feather} name="check" size={14} className="text-primary" />
                            )}
                          </Pressable>
                        );
                      })}
                    </View>
                  ))}
                </View>
              </TouchableWithoutFeedback>
              );
            })()}
          </View>
        </TouchableWithoutFeedback>
      </Modal>
    </View>
  );
}
